import { Box, Button, Flex, Grid, Image, Input, Spinner } from '@chakra-ui/react';
import { ethers } from 'ethers';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CARTRIDGE_ABI } from 'src/contracts/token/abi';
import { useBlockchainProvider } from 'src/providers/Blockchain/blockchainProvider';
import { nFormatter } from 'src/utils/utils';
import { DropdownItem } from '../DropdownItem/dropdownItem';

export const ManageTab = ({ address }: { address: string }) => {
	const [isLoading, setLoading] = useState<boolean>(true);
	const { account, contracts } = useBlockchainProvider();
	const [amount, setAmount] = useState<number>(0);
	const { t } = useTranslation();

	const [price, setPrice] = useState<number>(0);

	const [isRemoving, setIsRemoving] = useState<boolean>(false);
	const [isAdding, setIsAdding] = useState<boolean>(false);

	const cartridgeContract = useMemo(() => {
		const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
		if (provider) {
			const signer = provider.getSigner();
			return new ethers.Contract(address, CARTRIDGE_ABI, signer);
		} else {
			return null;
		}
	}, [window.ethereum]);

	const amountChanged = (e: any) => {
		e.target.value = Math.abs(e.target.value);
		setAmount(e.target.value);
	};

	const priceChanged = (e: any) => {
		e.target.value = Math.abs(e.target.value);
		setPrice(e.target.value);
	};

	const [storeSupply, setStoreSupply] = useState<number>(0);
	const [creatorSupply, setCreatorSupply] = useState<number>(0);

	useEffect(() => {
		(async () => {
			if (cartridgeContract && contracts.hashupStoreV2) {
				const store = await cartridgeContract.balanceOf(contracts.hashupStoreV2.address);
				const creator = await cartridgeContract.balanceOf(account);
				setStoreSupply(Number(store) / 100);
				setCreatorSupply(Number(creator) / 100);
				setLoading(false);
			}
		})();
	}, [cartridgeContract]);

	const addCartridges = async () => {
		if (cartridgeContract && contracts.hashupStoreV2) {
			const balance = await cartridgeContract.balanceOf(account);
			if (balance >= Math.floor(amount) * 100) {
				const transaction = await cartridgeContract.transfer(
					contracts.hashupStoreV2.address,
					Math.floor(amount) * 100
				);
				setIsAdding(true);
				await transaction.wait();
				setStoreSupply(storeSupply + Math.floor(amount));
				setCreatorSupply(creatorSupply - Math.floor(amount));

				setIsAdding(false);
			}
		}
	};

	const removeCartridges = async () => {
		if (contracts.hashupStoreV2) {
			const transaction = await contracts.hashupStoreV2.withdrawLicenses(address, Math.floor(amount) * 100);
			setIsRemoving(true);
			await transaction.wait();
			setCreatorSupply((prev) => prev + Math.floor(amount));
			setStoreSupply(storeSupply - Math.floor(amount) > 0 ? storeSupply - Math.floor(amount) : 0);
			setIsRemoving(false);
		}
	};

	const changePrice = async () => {
		if (contracts.hashupStoreV2) {
			const transaction = await contracts.hashupStoreV2.changeLicensePrice(address, Math.floor(price) * 10000);
			await transaction.wait();
		}
	};

	return (
		<Flex
			flexDir="column"
			gap="10px"
		>
			<DropdownItem heading={t('yourGames.manage.cartridgeSupply.heading')}>
				<Flex
					gridGap="30px"
					alignItems="flex-start"
				>
					<Grid
						gap="9px"
						mt="-3px"
						alignItems="flex-end"
					>
						<Box fontSize="12px">{t('yourGames.manage.cartridgeSupply.youHave')}</Box>
						<Box
							fontSize="16px"
							lineHeight="40%"
							fontWeight="bold"
						>
							{isLoading ? <Spinner /> : nFormatter(creatorSupply, 2)}
						</Box>
					</Grid>
					<Grid
						gap="5px"
						templateColumns="1fr 120px 1fr"
						alignItems="center"
					>
						<Button
							disabled={isRemoving}
							borderRadius="4px"
							fontWeight="normal"
							variant="ghost"
							onClick={removeCartridges}
						>
							{t('yourGames.manage.cartridgeSupply.take')}
						</Button>
						<Input
							type="number"
							border="1px solid #4E4F52"
							onChange={amountChanged}
							min={0}
							placeholder={t('yourGames.manage.cartridgeSupply.amount')}
							textAlign="center"
						/>
						<Button
							disabled={isAdding}
							borderRadius="4px"
							fontWeight="normal"
							onClick={addCartridges}
						>
							{t('yourGames.manage.cartridgeSupply.send')}
						</Button>
					</Grid>
					<Grid
						gap="9px"
						mt="-3px"
						alignItems="flex-end"
					>
						<Box fontSize="12px">{t('yourGames.manage.cartridgeSupply.inStore')}</Box>
						<Box
							fontSize="16px"
							lineHeight="40%"
							fontWeight="bold"
						>
							{isLoading ? <Spinner /> : nFormatter(storeSupply, 2)}
						</Box>
					</Grid>
				</Flex>
			</DropdownItem>
			<DropdownItem heading={t('yourGames.manage.price.heading')}>
				<Grid
					gap="9px"
					alignItems="flex-end"
				>
					<Box fontSize="12px">{t('yourGames.manage.price.newPrice')}</Box>
					<Grid
						gap="11px"
						templateColumns="200px 100px"
						alignItems="center"
					>
						<Input
							type="number"
							border="1px solid #4E4F52"
							prefix={'$'}
							onChange={priceChanged}
						/>
						<Button
							borderRadius="4px"
							onClick={changePrice}
						>
							{t('yourGames.manage.price.change')}
						</Button>
					</Grid>
				</Grid>
			</DropdownItem>
		</Flex>
	);
};
